import { Box, Grid, Typography, SvgIcon, SxProps, ThemeProvider } from '@mui/material';
import { Button } from '@components/button';
import { Link, useSearchParams } from 'react-router-dom';
import React, { ReactNode, useEffect } from 'react';
import { QuoteGetResponse } from '@lio/api-types';
import { Check } from '@assets/svgs';
import { useGetPolicy } from '@services/policy';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { Layout } from '@layout/Layout';
import { FullscreenLoading } from '@components/Loading';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import { SidePanel, SidePanelProps } from './SidePanel';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { useLayoutStore } from '@store/LayoutStore';
import { useComposeTheme } from '@theme/theme';
import { shallow } from 'zustand/shallow';
import { QuoteWithStore } from '@store/Quote/QuoteStore';

interface IPortalMessage {
  type: PortalMessageType;
  // if true show success regardless of type
  showSuccessCheck?: boolean;
  title: string[];
  subtitle: ReactNode[];
  secondarySubtitle?: ReactNode[];
  items?: string[];
  primaryBtnText?: string;
  primaryBtnAction?: string;
  secondaryBtnText?: string;
  secondaryBtnAction?: string;
  quote: QuoteGetResponse;
  multiPolicyQuoteBindings?: SidePanelProps['multiPolicyQuoteBindings'];
  sxProps?: {
    leftSide?: SxProps;
  };
}

export const PortalMessage = (props: IPortalMessage) => {
  return (
    <QuoteWithStore quoteId={props.quote.id}>
      <PortalMessageView {...props} />
    </QuoteWithStore>
  );
};

const PortalMessageView = ({
  type,
  title,
  subtitle,
  secondarySubtitle,
  showSuccessCheck,
  items,
  primaryBtnText,
  primaryBtnAction,
  secondaryBtnText,
  secondaryBtnAction,
  quote,
  multiPolicyQuoteBindings,
  sxProps,
}: IPortalMessage) => {
  const theme = useComposeTheme('dark', {
    themeOverride: 'dark',
  });

  const { isMobileScreen } = useGetScreenSize();

  const { closeHeaderMenu } = useLayoutStore(
    (state) => ({
      closeHeaderMenu: state.closeHeaderMenu,
    }),
    shallow,
  );

  const { data: policy } = useGetPolicy({
    policyId: quote?.policyId ? quote.policyId : undefined,
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const hasItems = items && items.length > 0;

  useEffect(() => {
    if (searchParams.has('sidebarItem')) {
      // ensure side menu is not open for blocking pages
      closeHeaderMenu();
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('sidebarItem');
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!quote) {
    return <FullscreenLoading />;
  }

  const premium = (quote.termPremium ?? 0) + (quote.termTaxFeeSurcharge ?? 0);
  const policyPolicyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '...';
  const formattedPolicyNumber = quote.policyId ? policyPolicyNumber : (quote.policyNumber?.policyNumber ?? '');

  const successMessages = [PortalMessageType.Bound, PortalMessageType.Bind, PortalMessageType.CustomerSubmission];

  return (
    <ThemeProvider theme={theme}>
      <Layout title='Quotes' config={{ padding: { xs: 1, sm: 1 } }}>
        <Grid
          container
          sx={{
            mt: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 0 },
            justifyContent: 'space-evenly',
            alignItems: 'center',
            height: { xs: 'auto', sm: 'calc(100vh - 160px)' },
          }}
        >
          <Box
            sx={{
              mb: { xs: 2, sm: 0 },
              width: { xs: '100vw', sm: '420px' },
              ...sxProps?.leftSide,
            }}
          >
            {showSuccessCheck || successMessages.includes(type) ? (
              <SvgIcon
                sx={{
                  color: 'secondary.dark',
                  width: 66,
                  height: 50,
                  padding: 0,
                }}
              >
                <Check height='100%' width='100%' style={{ marginBottom: '40px' }} />
              </SvgIcon>
            ) : null}
            {title.map((text: string) => (
              <Typography
                key={text}
                variant='h3'
                sx={{
                  '&:last-of-type': {
                    marginBottom: { xs: 2, sm: '30px' },
                  },
                }}
              >
                {text}
              </Typography>
            ))}
            {subtitle.map((element: any) => {
              if (React.isValidElement(element)) {
                const children = (element as React.ReactElement<{ children?: React.ReactNode }>).props.children;
                let key: string | number | undefined;
                if (Array.isArray(children)) {
                  key = children
                    .map((child) => (typeof child === 'string' || typeof child === 'number' ? child : ''))
                    .join(',');
                } else if (typeof children === 'string' || typeof children === 'number') {
                  key = children;
                } else {
                  key = undefined;
                }
                return (
                  <Typography
                    key={key}
                    variant='body'
                    sx={{
                      display: 'block',
                      marginLeft: '2px',
                      marginBottom: { xs: 1, sm: '20px' },
                      lineHeight: '1.5',
                    }}
                  >
                    {element}
                  </Typography>
                );
              }
              return null;
            })}
            {hasItems ? (
              <ul>
                {items.map((reason: string) => (
                  <li key={reason}>
                    <Typography variant='body'>{reason}</Typography>
                  </li>
                ))}
              </ul>
            ) : null}
            {secondarySubtitle
              ? secondarySubtitle.map((element: any) => {
                  if (React.isValidElement(element)) {
                    const children = (element as React.ReactElement<{ children?: React.ReactNode }>).props.children;
                    let key: string | number | undefined;
                    if (Array.isArray(children)) {
                      key = children
                        .map((child) => (typeof child === 'string' || typeof child === 'number' ? child : ''))
                        .join(',');
                    } else if (typeof children === 'string' || typeof children === 'number') {
                      key = children;
                    } else {
                      key = undefined;
                    }
                    return (
                      <Typography
                        key={key}
                        variant='body'
                        sx={{
                          display: 'block',
                          marginLeft: '2px',
                          margin: { xs: '10px 0', sm: '20px 0' },
                          lineHeight: '1.5',
                        }}
                      >
                        {element}
                      </Typography>
                    );
                  }
                  return null;
                })
              : null}
            <Box
              sx={{
                mb: { xs: 2, sm: 0 },
                textAlign: { xs: 'center', sm: 'left' },
                width: '100%',
              }}
            >
              {secondaryBtnText && secondaryBtnAction ? (
                <Link to={secondaryBtnAction} style={{ textDecoration: 'none' }}>
                  <Button fullWidth={isMobileScreen} variant='outlined' sx={{ marginTop: '20px', marginRight: '20px' }}>
                    {secondaryBtnText}
                  </Button>
                </Link>
              ) : null}
              {primaryBtnText && primaryBtnAction ? (
                <Link to={primaryBtnAction} style={{ textDecoration: 'none' }}>
                  <Button fullWidth={isMobileScreen} variant='contained' sx={{ marginTop: '20px' }}>
                    {primaryBtnText}
                  </Button>
                </Link>
              ) : null}
            </Box>
          </Box>
          <SidePanel
            quote={quote}
            multiPolicyQuoteBindings={multiPolicyQuoteBindings}
            type={type}
            premium={
              typeof premium === 'number' &&
              ![PortalMessageType.CustomerSubmission, PortalMessageType.CustomerUserBlocking].includes(type)
                ? premium
                : undefined
            }
            policyNumber={formattedPolicyNumber}
          />
        </Grid>
      </Layout>
    </ThemeProvider>
  );
};
